/**
 * Register page — POST /api/auth/register via AuthContext.
 *
 * Role is determined by the URL query parameter, never by an in-form
 * picker:
 *
 *     /register                  -> participant (default) + cross-link to vendor
 *     /register?as=participant   -> participant, no cross-link
 *     /register?as=vendor        -> dealer flow (Stripe Checkout on submit)
 *
 * This was Carlton's complaint #2: "the entry link already declared the
 * role, so the form shouldn't ask again". The in-form mode toggle has
 * been removed from the DOM entirely.
 */
import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Store } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { usePublicSettings } from "../../contexts/PublicSettingsContext";
import { api, extractApiError } from "../../lib/api";
import { Card, CardHeader, CardBody, CardFooter } from "../../components/ui/Card";
import { Input } from "../../components/ui/Input";
import { Button } from "../../components/ui/Button";

type SignupMode = "participant" | "dealer";

function resolveMode(raw: string | null): { mode: SignupMode; explicit: boolean } {
  if (!raw) return { mode: "participant", explicit: false };
  const lc = raw.toLowerCase();
  if (lc === "vendor" || lc === "dealer") return { mode: "dealer", explicit: true };
  // Treat any other value as "participant, but the user navigated here on purpose".
  return { mode: "participant", explicit: true };
}

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { settings } = usePublicSettings();

  const { mode, explicit } = resolveMode(params.get("as"));
  const isVendor = mode === "dealer";

  const [form, setForm] = React.useState({
    email: "",
    username: "",
    full_name: "",
    password: "",
    phone_number: "",
    contact_email: "",
  });
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});

  // Live values from platform_settings (Phase 31-B) — PublicSettingsContext
  // fetches these on app boot and falls back to the same defaults as the
  // backend model, so this never renders blank. Display-only; the real
  // price/commission are enforced server-side on the Stripe Checkout call.
  const dealerCommissionPct = Math.round(settings.dealer_commission_pct * 100);
  const participantCommissionPct = Math.round(settings.participant_commission_pct * 100);
  const subscriptionPrice = settings.dealer_subscription_price_usd;

  const update =
    (key: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value }));

  // Vendor-only required fields -- validated inline on blur, same pattern
  // as Sell.tsx's fieldErrors, kept file-local since only these two
  // fields on this one form need it.
  const validatePhoneNumber = (v: string): string | undefined =>
    v.trim() ? undefined : "Phone number is required.";
  const validateContactEmail = (v: string): string | undefined => {
    const t = v.trim();
    if (!t) return "Business contact email is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t)) return "Enter a valid email address.";
    return undefined;
  };

  const handleBlur = (key: "phone_number" | "contact_email") => () => {
    const validator = key === "phone_number" ? validatePhoneNumber : validateContactEmail;
    const msg = validator(form[key]);
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (msg) next[key] = msg;
      else delete next[key];
      return next;
    });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (isVendor) {
      const phoneErr = validatePhoneNumber(form.phone_number);
      const emailErr = validateContactEmail(form.contact_email);
      if (phoneErr || emailErr) {
        const next = { ...fieldErrors };
        if (phoneErr) next.phone_number = phoneErr;
        else delete next.phone_number;
        if (emailErr) next.contact_email = emailErr;
        else delete next.contact_email;
        setFieldErrors(next);
        return;
      }
    }

    setLoading(true);
    try {
      await register({
        ...form,
        phone_number: form.phone_number.trim(),
        contact_email: form.contact_email.trim(),
        role: isVendor ? "DEALER" : undefined,
      });

      if (isVendor) {
        // Account exists as PARTICIPANT (backend ignores any role on signup);
        // now route through Stripe to upgrade.
        try {
          const r = await api.post("/stripe/create-subscription-checkout", {
            origin_url: window.location.origin,
          });
          window.location.href = r.data.url;
          return;
        } catch (subErr) {
          setError(
            extractApiError(subErr) +
              " — your account is created; you can upgrade from the Dashboard.",
          );
          navigate("/profile", { replace: true });
          return;
        }
      }

      navigate("/", { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Registration failed.");
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------------------------------------------------
  // Per-role copy
  // -----------------------------------------------------------------------
  const headline = isVendor ? "Sign up as a Vendor" : "Sign up as a Participant";
  const lede = isVendor
    ? `Lower platform commission (${dealerCommissionPct}%), weekly featured-dealer rotation, public storefront. $${subscriptionPrice}/mo via Stripe.`
    : `Browse, buy, ask the forum, list parts when you've got them spare. Free, ${participantCommissionPct}% commission on anything you sell.`;

  return (
    <div className="mx-auto max-w-md px-4 py-10 md:py-16">
      <div className="flex justify-center mb-5" data-testid="register-logo">
        <img
          src="/logo.png"
          alt="Parts Buoy"
          className="h-16 w-16 md:h-20 md:w-20 object-contain"
        />
      </div>
      <Card elevated data-testid="register-card">
        <CardHeader>
          <div>
            <p
              className="text-xs uppercase tracking-[0.18em] font-bold text-brand mb-1"
              data-testid="register-mode-tag"
            >
              {isVendor ? "Vendor signup" : "Participant signup"}
            </p>
            <h1
              className="text-3xl font-black mb-1"
              data-testid="register-headline"
            >
              {headline}
            </h1>
            <p className="text-ink-muted text-sm">{lede}</p>
          </div>
        </CardHeader>
        <CardBody>
          {/* Cross-link to vendor flow when we landed on /register with no `as`. */}
          {!explicit && (
            <Link
              to="/register?as=vendor"
              data-testid="register-cross-link-vendor"
              className="mb-5 flex items-center gap-3 rounded-xl border-2 border-surface-border bg-surface-sunken px-4 py-3 hover:border-ink transition-colors"
            >
              <Store className="h-5 w-5 text-brand shrink-0" aria-hidden />
              <div className="flex-1 text-sm">
                <span className="font-semibold text-ink">Selling parts commercially? </span>
                <span className="text-ink-muted">Sign up as a Vendor →</span>
              </div>
            </Link>
          )}

          {/* Reverse cross-link when on the vendor flow, in case the user
              landed here by mistake. */}
          {isVendor && (
            <Link
              to="/register?as=participant"
              data-testid="register-cross-link-participant"
              className="mb-5 flex items-center gap-3 rounded-xl border-2 border-surface-border bg-surface-sunken px-4 py-3 hover:border-ink transition-colors"
            >
              <div className="flex-1 text-sm">
                <span className="font-semibold text-ink">Just buying or chatting? </span>
                <span className="text-ink-muted">Sign up as a Participant →</span>
              </div>
            </Link>
          )}

          <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
            <Input
              label="Email"
              type="email"
              autoComplete="email"
              required
              value={form.email}
              onChange={update("email")}
              data-testid="register-email"
            />
            <Input
              label="Username"
              type="text"
              autoComplete="username"
              required
              minLength={3}
              maxLength={40}
              value={form.username}
              onChange={update("username")}
              data-testid="register-username"
              hint="Choose a unique handle. Letters, numbers, and dashes work well."
            />
            <Input
              label="Full name (optional)"
              type="text"
              autoComplete="name"
              value={form.full_name}
              onChange={update("full_name")}
              data-testid="register-fullname"
            />
            {isVendor && (
              <Input
                label="Phone number"
                type="tel"
                required
                value={form.phone_number}
                onChange={update("phone_number")}
                onBlur={handleBlur("phone_number")}
                error={fieldErrors.phone_number}
                placeholder="+1 (555) 000-0000 or international format"
                hint="Buyers may contact you at this number. It will be visible on your storefront."
                data-testid="register-phone"
              />
            )}
            {isVendor && (
              <Input
                label="Business contact email"
                type="email"
                required
                value={form.contact_email}
                onChange={update("contact_email")}
                onBlur={handleBlur("contact_email")}
                error={fieldErrors.contact_email}
                placeholder="contact@yourbusiness.com"
                hint="Your public contact email for buyers. Can be different from your login email."
                data-testid="register-contact-email"
              />
            )}
            <Input
              label="Password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={form.password}
              onChange={update("password")}
              hint="At least 8 characters, including a letter and a number."
              data-testid="register-password"
            />

            {isVendor && (
              <div
                data-testid="register-dealer-note"
                className="rounded-xl border-2 border-brand bg-brand-softer text-brand-ink px-3 py-2 text-sm"
              >
                After creating your account we'll route you to Stripe to start
                your Vendor subscription. You can cancel from your dashboard at
                any time.
              </div>
            )}

            {error && (
              <div
                role="alert"
                data-testid="register-error"
                className="rounded-xl border-2 border-brand bg-brand-softer text-brand-ink px-3 py-2 text-sm font-medium"
              >
                {error}
              </div>
            )}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={loading}
              data-testid="register-submit"
            >
              {isVendor ? "Create account & subscribe" : "Create account"}
            </Button>
          </form>
        </CardBody>
        <CardFooter className="justify-between text-sm">
          <span className="text-ink-muted">Already have an account?</span>
          <Link
            to="/login"
            className="font-semibold text-brand hover:text-brand-hover"
            data-testid="register-login-link"
          >
            Sign in →
          </Link>
        </CardFooter>
      </Card>
    </div>
  );
}
